import { useState, useEffect, useRef } from "react";
import { ArrowRight, Share2, Check, Flame, Mail } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../lib/content";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { CtaBand } from "./Home";
import { useCommentary } from "../context/CommentaryContext";

const MAILTO = "mailto:pete@peteashbycoaching.com";
const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_FILMS = [
  { id: "truth", slug: "truth", title: "Truth comes first", video_url: "/video-truth.mp4", poster_url: "/video-truth.jpg", views: 0,
    synopsis: "Great leaders don't manage the truth – they surface it. Pete explains why CEOs and their teams should give the Board the bad news first, fast and unvarnished, and how that single habit builds the trust that keeps nasty surprises, and fraud, out of an organisation." },
  { id: "deep-dives", slug: "deep-dives", title: "Unscheduled deep dives", video_url: "/video-deepdives.mp4", poster_url: "/video-deepdives.jpg", views: 0,
    synopsis: "One of the most powerful tools a Board has is the freedom to drop into any part of the business, unannounced. Pete shows how random, unscheduled deep dives keep everyone honest – because no polished quarterly performance can survive a spotlight it never saw coming." },
  { id: "no-secret-zones", slug: "no-secret-zones", title: "No secret zones", video_url: "/video-nosecrets.mp4", poster_url: "/video-nosecrets.jpg", views: 0,
    synopsis: "Fraud composts in closed rooms. Pete makes the case for 'no secret zones' – every division, joint venture and special project kept in scope, the walls between them removed, and openness set as the default right across the organisation." },
];

const ShareFilm = ({ film, index }) => {
  const [copied, setCopied] = useState(false);
  const onShare = async () => {
    const url = `${window.location.origin}/on-leadership#${film.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${film.title} – Pete Ashby`, url });
        return;
      } catch (e) {
        /* cancelled */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* noop */
    }
  };
  return (
    <button
      type="button"
      onClick={onShare}
      data-testid={`leadership-share-${index}`}
      className="inline-flex items-center gap-2 mt-4 text-sm text-ink/80 hover:text-windsor transition-colors"
    >
      {copied ? <Check size={15} /> : <Share2 size={15} />}
      {copied ? "Link copied" : "Share this film"}
    </button>
  );
};

const LibraryCard = ({ it, i, hidden }) => {
  const captionText = (it.caption || "")
    .split(/\n{2,}|\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join("\n\n");
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const captionRef = useRef(null);

  useEffect(() => {
    const check = () => {
      const el = captionRef.current;
      if (!el || expanded) return;
      setOverflowing(el.scrollHeight > el.clientHeight + 2);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [captionText, expanded]);

  const collapsedStyle = {
    maxHeight: "116px",
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
  };
  const expandedStyle = {
    maxHeight: captionRef.current ? `${captionRef.current.scrollHeight + 40}px` : "2000px",
  };

  return (
    <article data-testid={`library-item-${i}`} className={hidden ? "hidden" : ""}>
      <div className="aspect-[4/5]">
        <img
          src={`${API}/api/library/file/${it.filename}`}
          alt={it.alt || it.title}
          width={it.width || undefined}
          height={it.height || undefined}
          loading="lazy"
          className="w-full h-full object-contain object-bottom drop-shadow-[0_12px_24px_rgba(42,32,26,0.15)]"
        />
      </div>
      <h3 className="font-serif font-semibold text-2xl text-ink leading-snug mt-5" data-testid={`library-title-${i}`}>
        {it.title}
      </h3>
      {captionText && (
        <div className="relative mt-4 max-w-[65ch]">
          <div
            ref={captionRef}
            id={`library-caption-${i}`}
            style={expanded ? expandedStyle : collapsedStyle}
            data-testid={`library-caption-${i}`}
            className="text-ink font-normal text-[17px] leading-[1.7] whitespace-pre-line overflow-hidden transition-[max-height] duration-500 ease-in-out"
          >
            {captionText}
          </div>
          {!expanded && overflowing && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-cloud to-transparent"
            />
          )}
          {overflowing && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={`library-caption-${i}`}
              data-testid={`library-readmore-${i}`}
              className="mt-3 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-windsor hover:text-windsor-dark transition-colors"
            >
              {expanded ? "Read less" : "Read more"}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

const ToughTrustLibrary = () => {
  const [items, setItems] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/library`)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d)) setItems(d);
      })
      .catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section className="bg-cloud py-24 md:py-32" data-testid="library-section">
      <div className="container-px">
        <Reveal>
          <p className="overline">The library</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink mt-4 max-w-2xl leading-tight">
            The Tough Trust library.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
          {items.map((it, i) => (
            <LibraryCard key={it.id} it={it} i={i} hidden={i >= 9 && !showAll} />
          ))}
        </div>
        {items.length > 9 && !showAll && (
          <div className="text-center mt-14">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              data-testid="library-show-more"
              className="inline-flex items-center gap-2 border border-ink/25 text-ink px-8 py-4 font-medium tracking-wide hover:bg-ink hover:text-ivory transition-colors"
            >
              Show more
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const NewsletterCapture = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("failed");
      setDone(true);
      toast.success("You're on the list – thanks!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-ink text-ivory py-24 md:py-28" data-testid="newsletter-section">
      <div className="container-px max-w-3xl mx-auto text-center">
        <Reveal>
          <span className="w-14 h-14 grid place-items-center rounded-full bg-windsor text-ivory mx-auto">
            <Mail size={22} />
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-6 leading-tight">
            Get Pete's next film.
          </h2>
          <p className="text-ivory/70 font-light text-lg mt-4 leading-relaxed">
            A short, no-nonsense film on trust and leadership, straight to your inbox – no more than
            once a month.
          </p>
          {done ? (
            <p className="mt-8 text-gold font-medium" data-testid="newsletter-thanks">
              Thank you – you'll hear from Pete soon.
            </p>
          ) : (
            <form
              onSubmit={submit}
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              data-testid="newsletter-form"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email"
                className="flex-1 bg-ivory/10 border border-ivory/25 px-5 py-3.5 text-ivory placeholder-ivory/40 focus:outline-none focus:border-windsor"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="bg-[#C2410C] text-ivory px-7 py-3.5 font-medium hover:bg-windsor-dark transition-colors disabled:opacity-60"
              >
                {busy ? "Adding…" : "Notify me"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default function OnLeadership() {
  const [films, setFilms] = useState(FALLBACK_FILMS);
  const viewed = useRef(new Set());
  const { stop } = useCommentary();

  useEffect(() => {
    fetch(`${API}/api/films`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length) setFilms(data);
      })
      .catch(() => {});
  }, []);

  const maxViews = Math.max(...films.map((f) => f.views || 0), 0);

  const recordView = (id) => {
    if (viewed.current.has(id)) return;
    viewed.current.add(id);
    fetch(`${API}/api/films/${id}/view`, { method: "POST" }).catch(() => {});
  };

  return (
    <div data-testid="on-leadership-page">
      <PageHero
        eyebrow="On Tough Trust"
        title="Ideas worth trusting."
        intro="Pete's films and reflections on trust, leadership, and the culture that keeps fraud and dishonesty out of great organisations."
        image={IMAGES.boardroomModern}
      />

      {/* FILM HUB */}
      <section className="bg-ivory py-24 md:py-32">
        <div className="container-px">
          <Reveal>
            <p className="overline">Films</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink mt-4 max-w-2xl leading-tight">
              Short films on the Method.
            </h2>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-8 mt-14">
            {films.map((f, i) => {
              const isMostWatched = maxViews > 0 && (f.views || 0) === maxViews;
              return (
                <Reveal key={f.id} delay={i * 0.08}>
                  <div id={f.slug} data-testid={`leadership-video-${i}`} className="scroll-mt-28">
                    <div className="relative">
                      {isMostWatched && (
                        <span
                          data-testid={`leadership-mostwatched-${i}`}
                          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-windsor text-ivory text-xs font-medium uppercase tracking-wide px-3 py-1.5"
                        >
                          <Flame size={13} /> Most watched
                        </span>
                      )}
                      <video
                        data-testid={`leadership-player-${i}`}
                        controls
                        playsInline
                        preload="none"
                        poster={f.poster_url}
                        onPlay={() => { recordView(f.id); stop(); }}
                        className="w-full aspect-video bg-black border border-ink/10 shadow-xl shadow-ink/10"
                      >
                        <source src={f.video_url} type="video/mp4" />
                      </video>
                    </div>
                    <h3 className="font-serif text-2xl text-ink leading-snug mt-5" data-testid={`leadership-title-${i}`}>
                      {f.title}
                    </h3>
                    {f.synopsis && (
                      <p className="text-ink/80 font-light leading-relaxed mt-2" data-testid={`leadership-synopsis-${i}`}>
                        {f.synopsis}
                      </p>
                    )}
                    <ShareFilm film={f} index={i} />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE TOUGH TRUST LIBRARY */}
      <ToughTrustLibrary />

      {/* STILL COACHES */}
      <section className="bg-cloud py-28 md:py-36" data-testid="still-coaches">
        <div className="container-px max-w-4xl text-center">
          <Reveal>
            <p className="overline">A personal note</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-tight tracking-tight mt-4">
              Pete also coaches one-to-one.
            </h2>
            <p className="text-ink/80 text-lg md:text-xl font-light leading-relaxed mt-6">
              Alongside his work with Boards and top teams, Pete takes on a small number of one-to-one
              coaching clients each year – CEOs, Directors and Board Chairs. If you'd like to explore
              working together, email Pete directly – he reads every message personally and replies
              within 48 hours.
            </p>
            <a
              href={MAILTO}
              data-testid="still-coaches-email"
              className="inline-flex items-center gap-2 mt-9 bg-[#C2410C] text-ivory px-8 py-4 font-medium hover:bg-windsor-dark transition-colors"
            >
              pete@peteashbycoaching.com <ArrowRight size={18} />
            </a>
          </Reveal>
        </div>
      </section>

      <NewsletterCapture />

      <CtaBand />
    </div>
  );
}
